import React, { useState, useCallback } from 'react';
import { Alert, Avatar, Button, Checkbox, Divider, Input, List, Modal, Select, Space, Spin, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';
import { Link } from 'react-router-dom';

import { ccyIsoCodeToSymbolMap } from '../../utils/CurrencyUtils';
import TrackerService from '../../utils/TrackerService';

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

const CONFIRM_WORD = 'CLOSE';
const TAG_COUNT_CAP = 50;

function money(cents, currency) {
  const symbol = ccyIsoCodeToSymbolMap[currency] || '';
  return `${symbol}${((Number(cents) || 0) / 100).toFixed(2)}`;
}

// A "manager" is the user-facing name for a team admin — they can be made the new owner.
// Admin status lives in roles:['admin'] OR the permissions.isTeamAdmin flag.
export function memberIsManager(m) {
  return !!(
    (Array.isArray(m.roles) && m.roles.includes('admin'))
    || (m.permissions && m.permissions.isTeamAdmin)
    || m.isTeamAdmin
  );
}

/**
 * "Close account" — the recoverable closure flow with a pre-flight cleanup checklist.
 * The checklist only GATHERS choices (forfeit balance, transfer teams); the actual
 * settle/forfeit/close/delete is done server-side by
 * clientService-closeAccountHandler-closeClientAccount. Self-service and admin.
 */
export default function CloseAccountSection({ client, clientId, isAdmin = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balances, setBalances] = useState([]);   // [{ currency, available }]
  const [teams, setTeams] = useState([]);          // [{ id, name, admins:[{clientId,displayName}], picked, transferred }]
  const [tagCount, setTagCount] = useState(0);
  const [tagCapped, setTagCapped] = useState(false);
  const [accessCount, setAccessCount] = useState(0);
  const [leaveCount, setLeaveCount] = useState(0);

  const [forfeit, setForfeit] = useState(false);
  const [removePayouts, setRemovePayouts] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const db = firebase.firestore();

  const loadData = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const clientRef = db.collection('clients').doc(clientId);

      // 1. Available balances (cents) per currency
      const accSnap = await clientRef.collection('accounts').where('version', '==', '2').get();
      const bals = [];
      accSnap.forEach((d) => {
        const a = d.data() || {};
        if (String(d.id).endsWith('-available') && Number(a.balance) > 0) {
          bals.push({ currency: a.currency, available: Number(a.balance) });
        }
      });
      setBalances(bals);

      // 2. Owned teams + their admins (to transfer to)
      const teamsSnap = await db.collection('teams').where('teamOwnerClientId', '==', clientId).get();
      const ownedTeams = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const t of teamsSnap.docs) {
        const td = t.data() || {};
        // eslint-disable-next-line no-await-in-loop
        const memSnap = await t.ref.collection('members').where('status', '==', 'approved').get();
        const managers = [];
        memSnap.forEach((m) => {
          const md = m.data() || {};
          if (md.clientId && md.clientId !== clientId && !md.isDeleted && memberIsManager(md)) {
            managers.push({ clientId: md.clientId, displayName: md.displayName || md.clientId });
          }
        });
        ownedTeams.push({
          id: t.id,
          name: td.teamName || td.displayName || t.id,
          image: td.topLogo || td.avatar || null,
          managers,
          picked: null,
          transferred: false,
          transferring: false,
        });
      }
      setTeams(ownedTeams);

      // 3. Tags that will pause (capped count)
      const tagSnap = await db.collection('tags').where('clientId', '==', clientId).limit(TAG_COUNT_CAP + 1).get();
      setTagCount(Math.min(tagSnap.size, TAG_COUNT_CAP));
      setTagCapped(tagSnap.size > TAG_COUNT_CAP);

      // 4. Delegated users who will lose access
      const delSnap = await clientRef.collection('delegated_users').get();
      setAccessCount(delSnap.size);

      // 5. Teams the client is a MEMBER of (not owner) → they'll leave these on close
      const ownedIds = new Set(ownedTeams.map((t) => t.id));
      const memberships = await db.collectionGroup('members').where('clientId', '==', clientId).get();
      let leaving = 0;
      memberships.forEach((m) => {
        const md = m.data() || {};
        if (!md.isDeleted && md.teamId && !ownedIds.has(md.teamId)) leaving += 1;
      });
      setLeaveCount(leaving);
    } catch (err) {
      console.error('[CloseAccountSection] load failed:', err);
      setError(err.message || 'Could not load account details.');
    } finally {
      setLoading(false);
    }
  }, [clientId, db]);

  const openModal = () => {
    setOpen(true);
    setForfeit(false);
    setRemovePayouts(false);
    setConfirmText('');
    setError(null);
    setDone(false);
    loadData();
  };

  const transferTeam = async (teamId, toClientId) => {
    if (!toClientId) return;
    setError(null);
    setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, transferring: true } : t)));
    try {
      await db.collection('teams').doc(teamId).set({ teamOwnerClientId: toClientId }, { merge: true });
      setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, transferred: true, transferring: false } : t)));
    } catch (err) {
      setTeams((prev) => prev.map((t) => (t.id === teamId ? { ...t, transferring: false } : t)));
      setError(`Could not transfer team: ${err.message || err}`);
    }
  };

  const hasBalance = balances.length > 0;
  const balanceOk = !hasBalance || forfeit;
  const confirmOk = confirmText.trim().toUpperCase() === CONFIRM_WORD;
  const canClose = balanceOk && confirmOk && !closing && !loading;

  const doClose = async () => {
    setClosing(true);
    setError(null);
    try {
      const fn = firebase.functions().httpsCallable('clientService-closeAccountHandler-closeClientAccount');
      await fn({ clientId, forfeitBalance: forfeit, deletePayoutDetails: removePayouts });
      try {
        const tracker = new TrackerService({});
        tracker.mixpanelTrack('credential_event', {
          type: 'account', action: 'account_closed', actor: isAdmin ? 'admin' : 'user', clientId,
        });
        tracker.mixpanelTrack('client_closed', {
          clientId, actor: isAdmin ? 'admin' : 'user', forfeited: forfeit, removedPayouts: removePayouts,
        });
      } catch (mpErr) { /* non-fatal */ }

      if (isAdmin) {
        setDone(true);
      } else {
        // Self-close: the callable deleted our auth user — sign out and leave.
        try { await firebase.auth().signOut(); } catch (e) { /* noop */ }
        window.location.href = '/';
      }
    } catch (err) {
      console.error('[CloseAccountSection] close failed:', err);
      setError(err.message || 'Could not close the account.');
    } finally {
      setClosing(false);
    }
  };

  // Already closed → show nothing actionable.
  if (client && client.accountClosed === true) {
    return (
      <div style={{ marginTop: 24 }}>
        <Alert type="info" showIcon message="This account is closed." />
      </div>
    );
  }

  return (
    <div style={{ marginTop: 24 }}>
      <Divider />
      <div style={{ padding: '12px 16px', border: '1px solid #ffccc7', background: '#fff2f0', borderRadius: 8 }}>
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          <Space>
            <WarningOutlined style={{ color: '#cf1322' }} />
            <Text strong style={{ color: '#cf1322' }}>Close account</Text>
          </Space>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {isAdmin
              ? 'Close this client account. Data is retained; sign-in methods are removed and access revoked.'
              : 'Closing removes all your sign-in methods, pauses your teams & qr/tags, and revokes others’ access. This action cannot be reversed.'}
          </Text>
          <Button danger onClick={openModal}>Close account…</Button>
        </Space>
      </div>

      <Modal
        title="Close account"
        visible={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
        getContainer={false}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : done ? (
          <Alert type="success" showIcon message="Account closed." description="Sign-in methods removed, access revoked, balance settled." />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* 1. Balance */}
            <div>
              <Title level={5} style={{ marginBottom: 4 }}>Balance</Title>
              {hasBalance ? (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {balances.map((b) => (
                    <Text key={b.currency}>Available: <Text strong>{money(b.available, b.currency)}</Text></Text>
                  ))}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    You must settle your balance before closing — withdraw it, or close without withdrawing it.
                  </Text>
                  <Space>
                    <Link to={`/client/manage/${clientId}`} onClick={() => setOpen(false)}>
                      <Button size="small">Withdraw first</Button>
                    </Link>
                  </Space>
                  <Checkbox checked={forfeit} onChange={(e) => setForfeit(e.target.checked)}>
                    I don’t want to withdraw my balance — close anyway.
                  </Checkbox>
                </Space>
              ) : (
                <Text type="secondary">No balance to settle.</Text>
              )}
            </div>

            {/* 2. Teams */}
            {teams.length > 0 && (
              <div>
                <Title level={5} style={{ marginBottom: 4 }}>Teams you own</Title>
                <List
                  size="small"
                  dataSource={teams}
                  renderItem={(t) => (
                    <List.Item key={t.id}>
                      <Space align="start" style={{ width: '100%' }}>
                        <Avatar shape="square" size={40} src={t.image || undefined}>
                          {(t.name || '?').charAt(0).toUpperCase()}
                        </Avatar>
                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                          <Text strong>{t.name}</Text>
                          {t.transferred ? (
                            <Text type="success" style={{ fontSize: 12 }}>✓ Ownership transferred — this team will keep running.</Text>
                          ) : t.managers.length === 0 ? (
                            <Text type="warning" style={{ fontSize: 12 }}>No other manager to transfer to — this team will be paused.</Text>
                          ) : (
                            <Space>
                              <Select
                                size="small"
                                style={{ minWidth: 200 }}
                                placeholder="Transfer to a manager…"
                                value={t.picked || undefined}
                                disabled={t.transferring}
                                onChange={(v) => setTeams((prev) => prev.map((x) => (x.id === t.id ? { ...x, picked: v } : x)))}
                              >
                                {t.managers.map((a) => (
                                  <Option key={a.clientId} value={a.clientId}>{a.displayName}</Option>
                                ))}
                              </Select>
                              <Button
                                size="small"
                                disabled={!t.picked || t.transferring}
                                loading={t.transferring}
                                onClick={() => transferTeam(t.id, t.picked)}
                              >
                                {t.transferring ? 'Transferring…' : 'Transfer'}
                              </Button>
                            </Space>
                          )}
                        </Space>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* 3 + 4. Notices */}
            <div>
              {tagCount > 0 && (
                <Paragraph type="secondary" style={{ marginBottom: 4 }}>
                  {tagCapped ? `${TAG_COUNT_CAP}+` : tagCount} tag{tagCount === 1 ? '' : 's'} will be paused.
                </Paragraph>
              )}
              {accessCount > 0 && (
                <Paragraph type="secondary" style={{ marginBottom: leaveCount > 0 ? 4 : 0 }}>
                  {accessCount} user{accessCount === 1 ? '' : 's'} with access will be removed.
                </Paragraph>
              )}
              {leaveCount > 0 && (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  You’ll leave {leaveCount} team{leaveCount === 1 ? '' : 's'} you’re a member of.
                </Paragraph>
              )}
            </div>

            {/* Payout details */}
            <div>
              <Checkbox checked={removePayouts} onChange={(e) => setRemovePayouts(e.target.checked)}>
                Also remove my saved bank/payout details.
              </Checkbox>
            </div>

            {/* 5. Confirm */}
            <div>
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 8 }}
                message="This cannot be undone from here"
                description={`Closing pauses your teams & qr/tags, removes all ${isAdmin ? 'their' : 'your'} sign-in methods, and revokes others’ access.${hasBalance ? (forfeit ? ' Your remaining balance will not be withdrawn.' : ' Settle your balance first.') : ''}`}
              />
              <Text>Type <Text strong>{CONFIRM_WORD}</Text> to confirm:</Text>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_WORD}
                style={{ marginTop: 4 }}
              />
            </div>

            {error && <Alert type="error" showIcon message={error} />}

            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={() => setOpen(false)} disabled={closing}>Cancel</Button>
              <Button danger type="primary" disabled={!canClose} loading={closing} onClick={doClose}>
                Close account
              </Button>
            </Space>
          </Space>
        )}
      </Modal>
    </div>
  );
}
